import { Component, Inject, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { finalize, firstValueFrom, of } from 'rxjs';
import {
  Filters,
  FnCaricamentoDati,
  SortBy,
} from 'src/app/components/lista-tabellare/classes/lista-tabellare-data-source';
import {
  BottoniListaEvent,
  Colonna,
  ListaTabellareComponent,
} from 'src/app/components/lista-tabellare/lista-tabellare.component';
import { SpinnerOverlayComponent } from 'src/app/components/spinner-overlay/spinner-overlay.component';
import {
  DoppiaMaterialitaService,
  MailRiepilogoDTO,
} from 'src/app/services/doppia-materialita/doppia-materialita.service';
import { UtilityService } from 'src/app/services/utility/utility.service';
import { FormatDatePipe } from 'src/app/pipes/format-date.pipe';

@Component({
  selector: 'app-dialog-riepilogo-invio-mail',
  templateUrl: './dialog-riepilogo-invio-mail.component.html',
  styleUrls: ['./dialog-riepilogo-invio-mail.component.scss'],
  standalone: false,
})
export class DialogRiepilogoInvioMailComponent {
  @ViewChild(SpinnerOverlayComponent) spinnerOver!: SpinnerOverlayComponent;
  @ViewChild('tabella') tabella!: ListaTabellareComponent;

  private readonly formatDatePipe = new FormatDatePipe();

  public colonne: { [key: string]: Colonna } = {
    stakeholder: {
      title: 'Stakeholder',
      value: (record: MailRiepilogoDTO) =>
        `${record?.nomeSh ?? ''} ${record?.cognomeSh ?? ''}`.trim(),
    },
    ragSoc: {
      title: 'Ragione Sociale',
      value: 'ragSocSh',
    },
    email: {
      title: 'Email',
      value: 'emailSh',
    },
    dataInvio: {
      title: 'Data Invio',
      value: (record: MailRiepilogoDTO) =>
        record?.dataInvio ? this.formatDatePipe.transform(record.dataInvio, true) : '',
    },
    esito: {
      title: 'Esito',
      align: 'center',
      value: (record: MailRiepilogoDTO) =>
        record?.esitoInvio
          ? '<div style="display:flex;justify-content:center;align-items:center;background-color:#CCDBCD;width:120px;text-align:center;border-radius:15px;height:27px"><p style="color:#3c6d00;font-size:13px;margin:0">Inviata</p></div>'
          : '<div style="display:flex;justify-content:center;align-items:center;background-color:#F8D7DA;width:120px;text-align:center;border-radius:15px;height:27px"><p style="color:#cf5b61;font-size:13px;margin:0">Non inviata</p></div>',
    },
    invia: {
      type: 'button',
      buttonIcon: 'mail',
      title: 'Invia mail',
      buttonId: 'invia',
      buttonMostraSempre: true,
      nascondiButton: (record: MailRiepilogoDTO) =>
        !!record?.esitoInvio || !!this.data?.isPermessoSoloRead,
    },
  };

  public fnCaricamentoDati: FnCaricamentoDati = (
    page: number,
    pageSize: number,
    ricerca?: string,
    filters?: Filters[],
    sortBy?: SortBy[],
  ) => {
    if (!this.data?.idMaterialita) {
      return of(false);
    }

    return this.doppiaMaterialitaService.getRiepilogoInvioMail(
      this.data.idMaterialita,
      page,
      pageSize,
      ricerca,
      filters,
      sortBy,
    );
  };

  constructor(
    private readonly doppiaMaterialitaService: DoppiaMaterialitaService,
    private readonly utilityService: UtilityService,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      idMaterialita: string;
      isPermessoSoloRead?: boolean;
    },
  ) {}

  public bottoniListaClick(event: BottoniListaEvent) {
    switch (event.id) {
      case 'invia':
        this.inviaSingolo(event.data);
        break;
      default:
        console.warn('ATTENZIONE: bottone ' + event.id + ' non riconosciuto');
    }
  }

  private async inviaSingolo(record: MailRiepilogoDTO) {
    if (!record?.id || this.data?.isPermessoSoloRead) return;

    const dialog = this.utilityService.openDialog({
      titolo: 'Confermare invio?',
      descrizione: `Sarà inviata la mail a ${record.emailSh}`,
      bottoni: [
        { nome_btn: 'No', id_btn: 'N' },
        { nome_btn: 'Si', id_btn: 'S' },
      ],
    });

    const valDialog = await firstValueFrom(dialog.beforeClosed());
    if (valDialog !== 'S') return;

    this.spinnerOver.show();
    this.doppiaMaterialitaService
      .postInvioMailSingoloSurvey(this.data.idMaterialita, record.id)
      .pipe(finalize(() => this.spinnerOver.hide()))
      .subscribe({
        next: () => {
          this.utilityService.openSnackBar('Mail inviata', '', {
            duration: 2000,
            panelClass: ['success-snackbar'],
          });
          this.tabella.caricaDati();
        },
        error: (err) => {
          console.error(err);
          this.utilityService.openSnackBar(
            err?.error?.message || 'Mail non inviata',
            '',
            { duration: 2000, panelClass: ['red-snackbar'] },
          );
        },
      });
  }

  public async inviaTutteNonInviate() {
    if (this.data?.isPermessoSoloRead) return;

    const dialog = this.utilityService.openDialog({
      titolo: 'Confermare invio?',
      descrizione: 'Saranno inviate le mail a tutti gli stakeholder non ancora contattati',
      bottoni: [
        { nome_btn: 'No', id_btn: 'N' },
        { nome_btn: 'Si', id_btn: 'S' },
      ],
    });

    const valDialog = await firstValueFrom(dialog.beforeClosed());
    if (valDialog !== 'S') return;

    this.spinnerOver.show();
    this.doppiaMaterialitaService
      .postInvioMailMultiploSurvey(this.data.idMaterialita)
      .pipe(finalize(() => this.spinnerOver.hide()))
      .subscribe({
        next: () => {
          this.utilityService.openSnackBar('Mail inviate', '', {
            duration: 2000,
            panelClass: ['success-snackbar'],
          });
          this.tabella.caricaDati();
        },
        error: (err) => {
          console.error(err);
          this.utilityService.openSnackBar(
            err?.error?.message || 'Invio non riuscito',
            '',
            { duration: 2000, panelClass: ['red-snackbar'] },
          );
        },
      });
  }
}
